import React, { useMemo, useState } from 'react';
import { average, formatMoney, formatQty } from '../../../utils/billToOrderCalc';
import { metricsForCatalogItem } from '../../../utils/billToDailySales';
import { FormulaInfoHint, HISTORICAL_AVG_FORMULAS } from './FormulaInfoHint';

const COLUMNS = [
  { key: 'itemCode', label: 'SKU', align: 'left' },
  { key: 'itemName', label: 'SKU Name', align: 'left' },
  { key: 'w1', label: 'W-1 Qty', align: 'right' },
  { key: 'w2', label: 'W-2 Qty', align: 'right' },
  { key: 'w3', label: 'W-3 Qty', align: 'right' },
  { key: 'avgQty', label: 'Avg Qty', align: 'right' },
  { key: 'upt1', label: 'UPT W-1', align: 'right' },
  { key: 'upt2', label: 'UPT W-2', align: 'right' },
  { key: 'upt3', label: 'UPT W-3', align: 'right' },
  { key: 'avgUpt', label: 'Avg UPT', align: 'right' },
  { key: 'rev1', label: 'W-1 Rev', align: 'right' },
  { key: 'rev2', label: 'W-2 Rev', align: 'right' },
  { key: 'rev3', label: 'W-3 Rev', align: 'right' },
  { key: 'totalRev', label: 'Total Revenue', align: 'right' },
];

function HistoricalAverageSalesTable({
  items,
  weeklyHistory,
  hint,
  emptyMessage,
  loading,
}) {
  const [skuSearch, setSkuSearch] = useState('');

  const historyRows = useMemo(() => {
    return (items || [])
      .map((item) => {
        const metrics = metricsForCatalogItem(weeklyHistory, item, 3);
        if (!metrics.fromApi) return null;
        return {
          ...item,
          w1: metrics.qty[0] || 0,
          w2: metrics.qty[1] || 0,
          w3: metrics.qty[2] || 0,
          avgQty: metrics.avgQty,
          upt1: metrics.upt[0],
          upt2: metrics.upt[1],
          upt3: metrics.upt[2],
          avgUpt: metrics.avgUpt,
          rev1: metrics.revenue[0] || 0,
          rev2: metrics.revenue[1] || 0,
          rev3: metrics.revenue[2] || 0,
          totalRev: metrics.totalRevenue,
          hasApiUpt: metrics.hasApiUpt,
        };
      })
      .filter(Boolean);
  }, [items, weeklyHistory]);

  const visibleRows = useMemo(() => {
    const q = skuSearch.trim().toLowerCase();
    if (!q) return historyRows;
    return historyRows.filter((row) =>
      [row.itemName, row.itemCode, row.subCategory].join(' ').toLowerCase().includes(q)
    );
  }, [historyRows, skuSearch]);

  const totals = useMemo(() => {
    return visibleRows.reduce(
      (acc, row) => ({
        w1: acc.w1 + row.w1,
        w2: acc.w2 + row.w2,
        w3: acc.w3 + row.w3,
        avgQty: acc.avgQty + row.avgQty,
        rev1: acc.rev1 + row.rev1,
        rev2: acc.rev2 + row.rev2,
        rev3: acc.rev3 + row.rev3,
        totalRev: acc.totalRev + row.totalRev,
      }),
      { w1: 0, w2: 0, w3: 0, avgQty: 0, rev1: 0, rev2: 0, rev3: 0, totalRev: 0 }
    );
  }, [visibleRows]);

  const hasApiUpt = visibleRows.some((row) => row.hasApiUpt);
  const totalUpt1 = hasApiUpt ? average(visibleRows.map((row) => row.upt1)) : null;
  const totalUpt2 = hasApiUpt ? average(visibleRows.map((row) => row.upt2)) : null;
  const totalUpt3 = hasApiUpt ? average(visibleRows.map((row) => row.upt3)) : null;
  const totalAvgUpt = hasApiUpt ? average(visibleRows.map((row) => row.avgUpt)) : null;

  const thClass = (align) =>
    `sticky top-0 z-10 border-b border-amber-200 bg-[#F7E5D5] px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-gray-700 ${
      align === 'right' ? 'text-right' : 'text-left'
    }`;
  const tdClass = (align, strong) =>
    `border-b border-gray-100 px-3 py-1.5 tabular-nums ${
      align === 'right' ? 'text-right' : 'text-left'
    } ${strong ? 'font-semibold text-gray-900' : 'text-gray-700'}`;
  const tfClass = (align) =>
    `sticky bottom-0 bg-[#FFF7F0] px-3 py-2 font-semibold tabular-nums text-gray-900 ${
      align === 'right' ? 'text-right' : 'text-left'
    }`;

  return (
    <div className="min-w-0 rounded-xl border border-gray-200 bg-white">
      <div className="flex flex-col gap-3 px-5 pt-4 pb-1 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h4 className="text-sm font-semibold text-gray-900">
            Historical Average Sales
            {historyRows.length ? (
              <span className="ml-1.5 font-medium text-gray-500">({historyRows.length})</span>
            ) : null}
          </h4>
          {hint ? <p className="mt-0.5 text-xs text-gray-500">{hint}</p> : null}
        </div>

        {historyRows.length ? (
          <input
            type="text"
            value={skuSearch}
            onChange={(e) => setSkuSearch(e.target.value)}
            placeholder="Search SKU NAME or Id"
            className="w-full rounded-lg border border-gray-200 bg-[#F7E5D5] px-3 py-1.5 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand sm:w-64"
          />
        ) : null}
      </div>

      <div className="px-5 py-1">
        <FormulaInfoHint
          title="Historical Average Sales — formulas"
          items={HISTORICAL_AVG_FORMULAS}
        />
      </div>
      {!items?.length ? (
        <p className="px-5 py-8 text-center text-sm text-gray-500">
          {emptyMessage || 'Select SKUs to see historical sales.'}
        </p>
      ) : loading && (!(weeklyHistory instanceof Map) || weeklyHistory.size === 0) ? (
        <p className="px-5 py-8 text-center text-sm text-gray-500">Loading historical sales…</p>
      ) : historyRows.length === 0 ? (
        <p className="px-5 py-8 text-center text-sm text-gray-500">
          No historical sales from Daily Sales for this selection.
        </p>
      ) : visibleRows.length === 0 ? (
        <p className="px-5 py-8 text-center text-sm text-gray-500">No SKUs match your search.</p>
      ) : (
        <div className="max-h-[440px] w-full min-w-0 overflow-auto">
          <table className="min-w-[1000px] border-collapse text-sm">
            <thead>
              <tr>
                {COLUMNS.map((col) => (
                  <th key={col.key} className={thClass(col.align)}>
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row) => (
                <tr key={row.id} className="hover:bg-amber-50/40">
                  <td className={tdClass('left')}>{row.itemCode}</td>
                  <td className={`${tdClass('left')} font-medium text-gray-900`}>{row.itemName}</td>
                  <td className={tdClass('right')}>{formatQty(row.w1, 2)}</td>
                  <td className={tdClass('right')}>{formatQty(row.w2, 2)}</td>
                  <td className={tdClass('right')}>{formatQty(row.w3, 2)}</td>
                  <td className={tdClass('right', true)}>{formatQty(row.avgQty, 2)}</td>
                  <td className={tdClass('right')}>
                    {row.hasApiUpt ? formatQty(row.upt1, 2) : '—'}
                  </td>
                  <td className={tdClass('right')}>
                    {row.hasApiUpt ? formatQty(row.upt2, 2) : '—'}
                  </td>
                  <td className={tdClass('right')}>
                    {row.hasApiUpt ? formatQty(row.upt3, 2) : '—'}
                  </td>
                  <td className={tdClass('right', true)}>
                    {row.hasApiUpt ? formatQty(row.avgUpt, 2) : '—'}
                  </td>
                  <td className={tdClass('right')}>{formatMoney(row.rev1)}</td>
                  <td className={tdClass('right')}>{formatMoney(row.rev2)}</td>
                  <td className={tdClass('right')}>{formatMoney(row.rev3)}</td>
                  <td className={tdClass('right', true)}>{formatMoney(row.totalRev)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td className={tfClass('left')} colSpan={2}>
                  Total ({visibleRows.length} SKUs)
                </td>
                <td className={tfClass('right')}>{formatQty(totals.w1, 2)}</td>
                <td className={tfClass('right')}>{formatQty(totals.w2, 2)}</td>
                <td className={tfClass('right')}>{formatQty(totals.w3, 2)}</td>
                <td className={tfClass('right')}>{formatQty(totals.avgQty, 2)}</td>
                <td className={tfClass('right')}>
                  {hasApiUpt ? formatQty(totalUpt1, 2) : '—'}
                </td>
                <td className={tfClass('right')}>
                  {hasApiUpt ? formatQty(totalUpt2, 2) : '—'}
                </td>
                <td className={tfClass('right')}>
                  {hasApiUpt ? formatQty(totalUpt3, 2) : '—'}
                </td>
                <td className={tfClass('right')}>
                  {hasApiUpt ? formatQty(totalAvgUpt, 2) : '—'}
                </td>
                <td className={tfClass('right')}>{formatMoney(totals.rev1)}</td>
                <td className={tfClass('right')}>{formatMoney(totals.rev2)}</td>
                <td className={tfClass('right')}>{formatMoney(totals.rev3)}</td>
                <td className={tfClass('right')}>{formatMoney(totals.totalRev)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

    </div>
  );
}

export default HistoricalAverageSalesTable;
